import React, { useEffect } from 'react';
import { useDryRun } from '../../../hooks/useDryRunWs';
import { ProductType } from '../../../hooks/useProducts';
import { useTailwindBreakpoint } from '../../../hooks/useTailwindBreakpoint';
import { Card } from '../../Card';
import { Divider } from '../../catalyst-ui-kit/divider';
import { useSyncExecutionPriceToFormState } from '../hooks/useSyncExecutionPriceToFormState';
import { TradePageWrapper } from '../TradePageWrapper';
import { TradeFormAccountSummary } from './TradeFormAccountSummary';
import { TradeFormHeader } from './TradeFormHeader';
import { TradeFormInfo } from './TradeFormInfo';
import { TradeFormInputs } from './TradeFormInputs';
import { TradeFormOptions } from './TradeFormOptions';
import { TradeFormOrderType } from './TradeFormOrderType';
import { TradeFormPrimaryButton } from './TradeFormPrimaryButton';
import { TradeFormSideTabs } from './TradeFormSideTabs';
import { TradeFormSlider } from './TradeFormSlider';
import { useTradeFormStore } from './TradeFormState';
import { TradeFormTpSl } from './TradeFormTpSl';

const ExecutionPriceSync: React.FC<{ productId: string }> = ({ productId }) => {
  const amount = useTradeFormStore((x) => x.amount);
  useSyncExecutionPriceToFormState(productId, amount);
  return null;
};

export const TradeFormUi: React.FC<{
  product: ProductType;
  TradeFormAccountHeader: typeof TradeFormHeader;
  TradeFormInfo: typeof TradeFormInfo;
  TradeFormSlider: typeof TradeFormSlider;
  TradeFormInputs: typeof TradeFormInputs;
  TradeFormPrimaryButton: typeof TradeFormPrimaryButton;
  TradeFormOrderType: typeof TradeFormOrderType;
  TradeFormSideTabs: typeof TradeFormSideTabs;
  TradeFormAccountSummary: typeof TradeFormAccountSummary;
  TradeFormTpSl: typeof TradeFormTpSl;
}> = ({
  product,
  TradeFormInfo,
  TradeFormSlider,
  TradeFormOrderType,
  TradeFormSideTabs,
  TradeFormPrimaryButton,
  TradeFormAccountSummary,
  TradeFormTpSl,
}) => {
  const isLg = useTailwindBreakpoint('lg');

  return (
    <div className="flex flex-col gap-1 lg:w-[280px] lg:min-w-[280px]">
      <Card as={TradePageWrapper} className="flex grow flex-col max-lg:min-h-[unset]">
        {isLg && (
          <>
            <TradeFormHeader product={product} />
            <Divider />
          </>
        )}
        {/* TODO, once errors are moved to the button we shouldn't need overflow here */}
        <div className="scrollbar-hide flex flex-1 flex-col justify-between p-3">
          <div className="space-y-3">
            <TradeFormOrderType product={product} />
            <TradeFormSideTabs />
            <TradeFormInputs product={product} />
            <TradeFormSlider product={product} />
            <TradeFormOptions />
            <TradeFormTpSl product={product} />
          </div>
          <div className="space-y-3">
            {isLg && <TradeFormPrimaryButton product={product} />}
            <TradeFormInfo product={product} />
          </div>
        </div>
      </Card>
      {isLg && (
        <Card className="flex-1">
          <TradeFormAccountSummary product={product} />
        </Card>
      )}
    </div>
  );
};

export const TradeForm: React.FC<{
  product: ProductType;
}> = ({ product }) => {
  const setLimitPrice = useTradeFormStore((x) => x.setLimitPrice);
  const setAmount = useTradeFormStore((x) => x.setAmount);
  const { reset } = useDryRun();

  useEffect(() => {
    setLimitPrice(undefined);
    setAmount(undefined);
    reset();
  }, [product.id, reset, setAmount, setLimitPrice]);

  return (
    <>
      <ExecutionPriceSync productId={product.id} />
      <TradeFormUi
        product={product}
        TradeFormAccountHeader={TradeFormHeader}
        TradeFormInfo={TradeFormInfo}
        TradeFormInputs={TradeFormInputs}
        TradeFormSlider={TradeFormSlider}
        TradeFormPrimaryButton={TradeFormPrimaryButton}
        TradeFormOrderType={TradeFormOrderType}
        TradeFormSideTabs={TradeFormSideTabs}
        TradeFormAccountSummary={TradeFormAccountSummary}
        TradeFormTpSl={TradeFormTpSl}
      />
    </>
  );
};
